import React from 'react'

import {
  BroadbandCableIcon,
  BroadbandCopperADSLIcon,
  BroadbandFullFibreIcon,
  BroadbandMobileIcon,
  BroadbandPartFibreIcon,
  DownloadIcon,
  HandCoinIcon
} from '~/assets/icons'
import { secondsToTimeString } from '~/components/deal/utils/date'
import { BroadbandService, DealProvider } from '~/gql/graphql'
import { formatSpeedToMbpsNumber } from '~/utils/format-speed'

import { Sections, TitleSection, Section } from './sections'
import styles from './styles.module.css'

const BroadbandContent = ({ broadband, provider }: { broadband: BroadbandService; provider: DealProvider }) => {
  const { downloadSpeed, uploadSpeed, estimatedDownloadTime, connectionType } = broadband

  const averageDownload = {
    title: 'Average download speed',
    amount: `${formatSpeedToMbpsNumber(downloadSpeed)} Mbps`
  }

  const averageUpload = {
    title: 'Average upload speed',
    amount: `${formatSpeedToMbpsNumber(uploadSpeed)} Mbps`,
    text: `*Average speeds are based on the speed available to at least 50% of customers during peak time (8 to 10pm). Your actual speeds may be higher or lower.`
  }

  const downloadHDFilm = {
    title: 'Time to download an HD film',
    amount: secondsToTimeString(estimatedDownloadTime),
    text: 'Based on downloading a 4GB file in high definition (HD) at the average speed for the package. Your actual speeds may be higher or lower, so the time shown is a rough guide.'
  }

  const compensation = {
    body: [
      `This provider has signed up to Ofcom's automatic compensation scheme, so you’ll get money back if something goes wrong without having to ask for it.`
    ],
    badge: 'Ofcom automatic compensation scheme',
    badgeIcon: <HandCoinIcon className={styles.icon} />
  }

  const usageLimits = {
    body: ['This lets you download as much as you want, whenever you want.'],
    badge: 'Unlimited downloads',
    badgeIcon: <DownloadIcon />
  }

  const broadbandTypes = [
    {
      title: 'Cable',
      icon: <BroadbandCableIcon />,
      body: [
        `Cable broadband uses a mix of fibre-optic and coaxial cables (the same used for cable TV) to deliver your internet.`,
        `It’s mainly used by Virgin Media so you’ll see it for a lot of their deals.`,
        `While it’s not as fast or reliable as full fibre,  the special cables they use from the cabinet are better than traditional copper phone lines.`
      ]
    },
    {
      title: 'Full fibre',
      icon: <BroadbandFullFibreIcon />,
      body: [
        `Full fibre, or fibre to the premises (FTTP), broadband uses fibre-optic cables to deliver your internet.`,
        `It’s more stable, reliable, and speedy than other broadband types - especially at peak times - as it doesn’t use older copper wires.`,
        `Though it’s not yet widely available across the UK.`
      ]
    },
    {
      title: 'Part fibre',
      icon: <BroadbandPartFibreIcon />,
      body: [
        `Part fibre, or fibre to the cabinet (FTTC), broadband uses a mix of fibre-optic cables and copper wires to deliver your internet.`,
        `It’s widely available across the UK, and faster than fully copper ADSL, but not as fast or reliable as full fibre.`,
        `With copper wiring, your distance from the telephone exchange affects speeds and reliability - if your home is further away, it’s likely your speeds will be slower.`
      ]
    },
    {
      title: 'Copper ADSL',
      icon: <BroadbandCopperADSLIcon />,
      body: [
        'Copper ADSL uses copper phone lines to deliver your internet.',
        `It’s available across most of the UK, and may be the best option if you live in a rural area.`,
        `But it’s the oldest and slowest type and can be unreliable.`
      ]
    },

    {
      title: 'Mobile',
      icon: <BroadbandMobileIcon />,
      body: [
        `Mobile broadband delivers your internet over a wireless 4G or 5G network.`,
        `It’s a great option if you can’t get fixed-line broadband, or you live very far from a telephone exchange and need faster speeds.`,
        `But it’s not available everywhere, and it may not be right if you struggle to get phone signal at home. It can also have usage limits so check this before you sign up.`
      ]
    }
  ]

  const unknownType = connectionType === 'unknown'
  const currentType = broadbandTypes.filter((type) => type.title === connectionType)
  const otherTypes = broadbandTypes.filter((type) => type.title !== connectionType)

  const ofcomSchemeProviders = [
    '12153', // 'BT',
    '12774', // 'Hyperoptic',
    '12827', // 'NOW Broadband',
    '12185', // 'Plusnet'
    '12175', // 'Sky',
    '12165', // 'TalkTalk',
    '12179', // Virgin Media
    '12193' // Vodaphone
  ]

  const isInOfcomScheme = !!ofcomSchemeProviders.find((id) => id === provider.id)

  return (
    <>
      <h6 className={styles.header}>Speeds</h6>
      <TitleSection content={averageDownload} boldAmount smallHeader border />
      <TitleSection content={averageUpload} boldAmount smallHeader border />
      <TitleSection content={downloadHDFilm} boldAmount smallHeader border />

      {isInOfcomScheme && (
        <div className={styles.sections} data-has-border={true}>
          <h6 className={styles.header}>Compensation</h6>
          <Section section={compensation} stacked largeBody />
        </div>
      )}

      <div className={styles.sections} data-has-border={true}>
        <h6 className={styles.header}>Usage limits</h6>
        <Section section={usageLimits} stacked largeBody />
      </div>

      <h6 className={styles.header}>Broadband type</h6>
      {unknownType ? (
        <>
          <Section
            section={{
              body: [`We can’t confirm the broadband type for your address, but you can check this with the provider.`]
            }}
            largeBody
          />
          <h6 className={styles.header} data-small-margin>
            Compare broadband types
          </h6>
        </>
      ) : (
        <>
          <Sections sections={currentType} broadbandType />
          <h6 className={styles.header} data-small-margin>
            Compare to other broadband types
          </h6>
        </>
      )}

      <Sections sections={unknownType ? broadbandTypes : otherTypes} broadbandType accordion />
    </>
  )
}

export default BroadbandContent
